import { useState } from "react";
import { api, type CipherMode, type Room, type User } from "../lib/api";
import { CIPHER_LABELS } from "../lib/crypto";

interface Props {
  user: User;
  rooms: Room[];
  active: Room | null;
  onSelect: (r: Room) => void;
  onChanged: () => Promise<Room[]>;
  onLogout: () => void;
}

export function Rooms({ user, rooms, active, onSelect, onChanged, onLogout }: Props) {
  const [label, setLabel] = useState("");
  const [mode, setMode] = useState<CipherMode>("psk");
  const [joinId, setJoinId] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function create() {
    setError(null);
    try {
      const { room } = await api.createRoom(label.trim() || undefined, mode);
      setLabel("");
      await onChanged();
      onSelect(room);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function join() {
    setError(null);
    try {
      const { room } = await api.joinRoom(joinId.trim());
      setJoinId("");
      await onChanged();
      onSelect(room);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div className="sidebar">
      <h1>
        whitenoise
        <small>signed in as {user.username}</small>
      </h1>
      <div className="room-list">
        {rooms.length === 0 && <p className="muted" style={{ padding: 8 }}>No rooms yet.</p>}
        {rooms.map((r) => (
          <div
            key={r.id}
            className={`room-item ${active?.id === r.id ? "active" : ""}`}
            onClick={() => onSelect(r)}
          >
            <div>{r.label || "(unlabeled room)"}</div>
            <div className="id">{r.id}</div>
          </div>
        ))}
      </div>
      <div className="sidebar-actions">
        {error && <p className="error">{error}</p>}
        <div style={{ display: "flex", gap: 6 }}>
          <input placeholder="New room label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} />
          <button className="primary" onClick={create}>＋</button>
        </div>
        <select value={mode} onChange={(e) => setMode(e.target.value as CipherMode)} title="Encryption mode for the new room">
          {(Object.keys(CIPHER_LABELS) as CipherMode[]).map((m) => (
            <option key={m} value={m}>{CIPHER_LABELS[m]}</option>
          ))}
        </select>
        <div style={{ display: "flex", gap: 6 }}>
          <input placeholder="Join by room id…" value={joinId} onChange={(e) => setJoinId(e.target.value)} />
          <button onClick={join}>Join</button>
        </div>
        <button className="ghost" onClick={onLogout}>Sign out</button>
      </div>
    </div>
  );
}
